import type { CSSProperties } from "react";
import { connect, ConnectedProps } from "react-redux";
import { useNavigate, useLocation } from "react-router";
import { logout } from "../Auth/authThunks";
import { PATH } from "../../Constants/paths";
import { TOUR_TARGET } from "../../Constants/tutorialSteps";
import { useTutorial } from "../../context/TutorialContext";
import { useClientCapabilities } from "../../hooks/useClientCapabilities";
import { CAPABILITY_KEYS } from "../../utils/capabilities";
import { useAssistantAvailability } from "../../hooks/useAssistantAvailability";

const sections = [
  {
    label: "Hotlist",
    icon: "fa-list-check",
    accent: "var(--color-hotlist)",
    activeBg: "rgba(var(--rgb-hotlist), 0.1)",
    path: "/sales-home",
    selectedOnPaths: ["/sales-home"],
    tourTarget: TOUR_TARGET.NAV_HOTLIST,
    capability: CAPABILITY_KEYS.HOTLIST,
  },
  {
    label: "Accounts",
    icon: "fa-building",
    accent: "var(--account-insight-color)",
    activeBg: "rgba(var(--rgb-account), 0.08)",
    path: "/all-accounts",
    selectedOnPaths: ["/all-accounts", "/account/*", "/crm/reviews"],
    tourTarget: TOUR_TARGET.NAV_ACCOUNTS,
    capability: CAPABILITY_KEYS.ACCOUNTS,
  },
  {
    label: "Insights",
    icon: "fa-lightbulb",
    accent: "var(--industry-insight-color)",
    activeBg: "rgba(var(--rgb-industry), 0.08)",
    path: "/insights",
    selectedOnPaths: ["/insights"],
    tourTarget: TOUR_TARGET.NAV_INSIGHTS,
    capability: CAPABILITY_KEYS.INDUSTRIES,
  },
  {
    label: "Assets",
    icon: "fa-folder-open",
    accent: "var(--asset-insight-color)",
    activeBg: "rgba(var(--rgb-asset), 0.1)",
    path: "/my-assets",
    selectedOnPaths: ["/my-assets"],
    tourTarget: TOUR_TARGET.NAV_ASSETS,
    capability: CAPABILITY_KEYS.ASSETS,
  },
] as const;

const mapDispatchToProps = { logout };
const connector = connect(null, mapDispatchToProps);
interface OwnProps {
  isCollapsed?: boolean;
  onToggleCollapsed?: () => void;
}

interface Props extends ConnectedProps<typeof connector>, OwnProps {}

const _NavigationModule = ({ logout, isCollapsed = false, onToggleCollapsed }: Props) => {
  const navigate = useNavigate();
  const currentPath = useLocation().pathname;
  const { openTutorial } = useTutorial();
  const { hasCapability } = useClientCapabilities();
  const assistant = useAssistantAvailability();

  const goToSectionPath = (path: string) => {
    navigate(path);
  };

  const handleSettingsClick = () => {
    navigate(PATH.SETTINGS);
  };

  const handleLogout = async () => {
    await logout();
    navigate(PATH.LANDING);
  };

  const isSettingsActive = currentPath === PATH.SETTINGS;

  const renderNavButton = (
    label: string,
    icon: string,
    onClick: () => void,
    options?: {
      isActive?: boolean;
      isDanger?: boolean;
      isGuide?: boolean;
      tourTarget?: string;
      accent?: string;
      activeBg?: string;
    },
  ) => {
    const isActive = options?.isActive ?? false;
    const className = [
      "navigationModule_section",
      isActive ? "navigationModule_section--active" : "",
      options?.isDanger ? "navigationModule_section--danger" : "",
      options?.isGuide ? "navigationModule_section--guide" : "",
    ]
      .filter(Boolean)
      .join(" ");

    return (
      <button
        key={label}
        type="button"
        className={className}
        onClick={onClick}
        aria-current={isActive ? "page" : undefined}
        title={label}
        data-tour={options?.tourTarget}
        style={
          isActive
            ? ({
                "--nav-accent": options?.accent ?? "var(--navy-blue-color)",
                "--nav-active-bg": options?.activeBg ?? "rgba(var(--rgb-navigation), 0.08)",
              } as CSSProperties)
            : undefined
        }
      >
        <span className="navigationModule_sectionIcon" aria-hidden>
          <i className={`fa-solid ${icon}`} />
        </span>
        <span className="navigationModule_sectionText">{label}</span>
      </button>
    );
  };

  return (
    <nav className="navigationModule_container" aria-label="Main">
      {onToggleCollapsed && (
        <div className="navigationModule_collapseRow">
          <button
            type="button"
            className="navigationModule_collapseButton"
            onClick={onToggleCollapsed}
            aria-label={isCollapsed ? "Expand navigation" : "Collapse navigation"}
            title={isCollapsed ? "Expand navigation" : "Collapse navigation"}
            aria-expanded={!isCollapsed}
          >
            <i
              className={`fa-solid ${isCollapsed ? "fa-chevron-right" : "fa-chevron-left"}`}
              aria-hidden
            />
          </button>
        </div>
      )}

      <div className="navigationModule_content">
        {assistant.enabled && hasCapability(CAPABILITY_KEYS.ACCOUNTS) && renderNavButton("Ask Intentico", "fa-sparkles", () => goToSectionPath(PATH.ASK_INTENTICO), { isActive: currentPath === PATH.ASK_INTENTICO })}
        {sections.filter((section) => hasCapability(section.capability)).map((section) => {
          const isSelected = section.selectedOnPaths.some((pattern) => {
            const regex = new RegExp(`^${pattern.replace(/\*/g, ".*")}$`);
            return regex.test(currentPath);
          });

          return renderNavButton(section.label, section.icon, () => goToSectionPath(section.path), {
            isActive: isSelected,
            accent: section.accent,
            activeBg: section.activeBg,
            tourTarget: section.tourTarget,
          });
        })}
      </div>

      <div className="navigationModule_footer">
        <div className="navigationModule_footerDivider" role="separator" />
        {renderNavButton("App Guide", "fa-graduation-cap", openTutorial, {
          isGuide: true,
          accent: "var(--color-sky)",
          activeBg: "color-mix(in srgb, var(--color-info-subtle) 85%, transparent)",
        })}
        {renderNavButton("Account Settings", "fa-gear", handleSettingsClick, {
          isActive: isSettingsActive,
          tourTarget: TOUR_TARGET.NAV_SETTINGS,
        })}
        {renderNavButton("Sign Out", "fa-arrow-right-from-bracket", handleLogout, {
          isDanger: true,
        })}
      </div>
    </nav>
  );
};

export const NavigationModule = connector(_NavigationModule);
